import { useState } from "react";
import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumb from "@/components/base/Breadcrumb";
import FaqAccordion from "@/components/feature/FaqAccordion";
import { useSeo } from "@/hooks/useSeo";
import { faqs } from "@/mocks/faq";
import { categories } from "@/mocks/categories";
import { site } from "@/mocks/site";
import { submitReaddyForm } from "@/utils/submitForm";

const SUBMIT_URL = "https://readdy.ai/api/form/dahflq3oh653ivfvntfg";
const MAX_LEN = 500;

export default function Submit() {
  const [detailLen, setDetailLen] = useState(0);
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [feedback, setFeedback] = useState("");

  useSeo({
    title: "我要投稿 - 提交爆料线索与素材 | 网曝吃瓜",
    description:
      "在线提交你的爆料线索、素材与原帖信息。网曝吃瓜支持匿名投稿，只需提供可核对的时间与过程描述，编辑部将在 24 小时内完成初筛并跟进整理。",
    keywords: "投稿,吃瓜投稿,爆料投稿,线索提交,网友投稿,网曝吃瓜投稿",
    ogType: "website",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "ContactPage",
        name: "我要投稿 - 网曝吃瓜",
        description: "提交爆料线索、素材与原帖信息的投稿页面。",
        inLanguage: "zh-CN",
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://wangbaochigua.example/" },
          { "@type": "ListItem", position: 2, name: "我要投稿" },
        ],
      },
    ],
  });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formEl = e.currentTarget;
    setStatus("loading");
    setFeedback("");

    const result = await submitReaddyForm(
      SUBMIT_URL,
      formEl,
      "contact_alt",
      "投稿已收到，编辑部会在 24 小时内完成初筛。如有需要补充的细节，我们会通过邮箱联系你。"
    );

    if (result.ok) {
      setStatus("success");
      setFeedback(result.message);
      setDetailLen(0);
      formEl.reset();
    } else {
      setStatus("error");
      setFeedback(result.message);
    }
  };

  const inputClass =
    "w-full h-11 px-3.5 text-sm rounded-md bg-background-50 border border-background-200 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300";

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1">
        <section className="w-full border-b border-background-200 bg-background-100">
          <div className="w-full px-4 md:px-6 py-8 md:py-12">
            <Breadcrumb items={[{ label: "我要投稿" }]} />
            <h1 className="mt-5 text-3xl md:text-4xl font-bold text-foreground-950">把线索交给我们</h1>
            <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed max-w-2xl">
              不需要实名，也不需要写得漂亮。把时间、地点、发生了什么说清楚就够了——剩下的整理工作交给编辑部。
              信息越具体，被采用的几率越高。
            </p>
          </div>
        </section>

        <div className="w-full px-4 md:px-6 py-10 md:py-14 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          <div className="lg:col-span-8">
            {status === "success" ? (
              <div className="p-6 md:p-8 rounded-lg bg-secondary-50 border border-secondary-200">
                <span className="w-12 h-12 flex items-center justify-center rounded-md bg-secondary-500 text-background-50">
                  <i className="ri-check-line text-2xl" aria-hidden="true" />
                </span>
                <h2 className="mt-4 text-xl font-bold text-secondary-950">投稿已提交</h2>
                <p className="mt-2 text-sm md:text-base text-secondary-900 leading-relaxed">{feedback}</p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setStatus("idle");
                      setFeedback("");
                    }}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-add-line" aria-hidden="true" />
                    再投一条
                  </button>
                  <Link
                    to="/"
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md border border-secondary-300 text-secondary-900 text-sm hover:bg-secondary-100 transition-colors whitespace-nowrap"
                  >
                    返回首页
                  </Link>
                </div>
              </div>
            ) : (
              <form
                data-readdy-form
                id="tip-submit-form"
                onSubmit={handleSubmit}
                className="p-6 md:p-8 rounded-lg bg-background-100 border border-background-200"
                noValidate
              >
                <div className="assist-field-wrap" aria-hidden="true">
                  <label htmlFor="contact_alt">备用联系方式（请勿填写）</label>
                  <input
                    id="contact_alt"
                    name="contact_alt"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    readOnly
                    aria-hidden="true"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="nickname" className="block text-sm font-medium text-foreground-800 mb-1.5">
                      昵称 <span className="text-foreground-400 font-normal">（可匿名）</span>
                    </label>
                    <input id="nickname" name="nickname" type="text" placeholder="例如：老陈" className={inputClass} />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground-800 mb-1.5">
                      联系邮箱 <span className="text-primary-600">*</span>
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      placeholder="用于核实细节，不会公开"
                      className={inputClass}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                  <div>
                    <label htmlFor="category" className="block text-sm font-medium text-foreground-800 mb-1.5">
                      线索类型
                    </label>
                    <select id="category" name="category" className={`${inputClass} cursor-pointer`} defaultValue="entertainment">
                      {categories.map((c) => (
                        <option key={c.slug} value={c.name}>
                          {c.name}
                        </option>
                      ))}
                      <option value="其他">其他 / 不确定</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="happened_at" className="block text-sm font-medium text-foreground-800 mb-1.5">
                      发生时间
                    </label>
                    <input id="happened_at" name="happened_at" type="date" className={`${inputClass} cursor-pointer`} />
                  </div>
                </div>

                <div className="mt-4">
                  <label htmlFor="title" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    线索标题 <span className="text-primary-600">*</span>
                  </label>
                  <input
                    id="title"
                    name="title"
                    type="text"
                    required
                    placeholder="一句话概括，例如：某商场促销规则前后不一致"
                    className={inputClass}
                  />
                </div>

                <div className="mt-4">
                  <label htmlFor="description" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    详细描述 <span className="text-primary-600">*</span>
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    required
                    maxLength={MAX_LEN}
                    rows={7}
                    onChange={(e) => setDetailLen(e.target.value.length)}
                    placeholder="请尽量写清时间、地点与完整过程。相比结论，我们更需要过程。"
                    className="w-full p-3.5 text-sm rounded-md bg-background-50 border border-background-200 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 resize-y"
                  />
                  <p className="mt-1.5 text-xs text-foreground-400 text-right">
                    {detailLen} / {MAX_LEN} 字
                  </p>
                </div>

                <div className="mt-4">
                  <label htmlFor="material_link" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    素材链接 <span className="text-foreground-400 font-normal">（可选）</span>
                  </label>
                  <input
                    id="material_link"
                    name="material_link"
                    type="url"
                    placeholder="图片或视频的公开链接，可留空"
                    className={inputClass}
                  />
                </div>

                <label htmlFor="agree" className="mt-5 flex items-start gap-2.5 cursor-pointer">
                  <input
                    id="agree"
                    name="agree"
                    type="checkbox"
                    value="同意内容原则"
                    className="mt-0.5 w-4 h-4 rounded border-background-300 text-primary-500 focus:ring-primary-300 cursor-pointer"
                  />
                  <span className="text-xs md:text-sm text-foreground-600 leading-relaxed">
                    我已阅读并同意网站的内容原则，确认所提交内容不包含人身攻击与未经核实的指控。
                  </span>
                </label>

                {status === "error" && (
                  <p className="mt-4 p-3 rounded-md bg-primary-50 border border-primary-200 text-sm text-primary-800" role="alert">
                    {feedback}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={status === "loading"}
                  className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <i className={status === "loading" ? "ri-loader-4-line animate-spin" : "ri-send-plane-fill"} aria-hidden="true" />
                  {status === "loading" ? "提交中…" : "提交线索"}
                </button>
              </form>
            )}
          </div>

          <aside className="lg:col-span-4 flex flex-col gap-6">
            <section className="p-5 rounded-lg bg-accent-50 border border-accent-200">
              <h2 className="text-base font-bold text-foreground-950 flex items-center gap-2">
                <i className="ri-lightbulb-line text-accent-700" aria-hidden="true" />
                更容易被采用的写法
              </h2>
              <ul className="mt-4 space-y-3">
                {[
                  "写明具体日期与时间段，而不是「前几天」。",
                  "描述完整过程，而不是只给一个结论。",
                  "提供可公开访问的原始素材链接，避免二次转存。",
                  "不确定的部分直接标注，不用猜来补全。",
                ].map((tip) => (
                  <li key={tip} className="flex gap-2.5 text-sm text-foreground-700 leading-relaxed">
                    <i className="ri-arrow-right-s-line text-accent-700 mt-0.5 shrink-0" aria-hidden="true" />
                    {tip}
                  </li>
                ))}
              </ul>
            </section>

            <section className="p-5 rounded-lg bg-foreground-950 text-background-100">
              <h2 className="text-base font-bold flex items-center gap-2">
                <i className="ri-shield-check-line text-accent-400" aria-hidden="true" />
                隐私说明
              </h2>
              <p className="mt-3 text-sm text-background-300 leading-relaxed">
                你填写的邮箱仅用于编辑部内部核实，不会出现在公开页面，也不会提供给第三方。
                如果你希望完全匿名，昵称可以留空。
              </p>
              <a
                href={`mailto:${site.tipEmail}`}
                rel="nofollow"
                className="mt-4 inline-flex items-center gap-2 text-sm text-accent-300 hover:text-accent-200 transition-colors break-all"
              >
                <i className="ri-mail-line" aria-hidden="true" />
                {site.tipEmail}
              </a>
            </section>
          </aside>
        </div>

        <div className="bg-background-100/60">
          <FaqAccordion
            items={faqs.slice(0, 4)}
            idPrefix="submit-faq"
            eyebrow="投稿答疑"
            heading="提交之前，先看看这些"
            intro="关于匿名、审核时间与素材要求，这里都有明确说明。"
          />
        </div>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}